import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance as axios } from '../../../context/AuthContext';
import { ProReviewDialog } from './ProReviewDialog';

const STATUS_STYLES = {
  pending: 'bg-blue-100 text-blue-800',
  info_requested: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  rejected: 'bg-gray-200 text-gray-700',
};
export const STATUS_LABELS = {
  pending: 'Pending Verification', info_requested: 'Info Requested', approved: 'Approved',
  suspended: 'Suspended', rejected: 'Rejected',
};
const FILTERS = [
  ['all', 'All'], ['pending', 'Pending'], ['info_requested', 'Info requested'], ['approved', 'Approved'],
  ['needs_verification', 'Needs verification'], ['suspended', 'Suspended'], ['complaints', 'Open complaints'],
];
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const CountCard = ({ label, value, testId }) => (
  <div className="bg-white border rounded-xl p-4" data-testid={testId}>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

export default function OversightTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    axios.get('/api/directory/admin/oversight').then(r => setData(r.data)).catch(() => setData({ counts: {}, professionals: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-gray-500 p-4">Loading…</p>;

  const c = data.counts;
  const rows = data.professionals.filter(p => (
    filter === 'all' ? true
      : filter === 'needs_verification' ? p.needs_verification
      : filter === 'complaints' ? p.open_complaints > 0
      : p.status === filter
  ));

  return (
    <div data-testid="oversight-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <CountCard label="Pending" value={c.pending || 0} testId="count-pending" />
        <CountCard label="Info requested" value={c.info_requested || 0} testId="count-info-requested" />
        <CountCard label="Approved" value={c.approved || 0} testId="count-approved" />
        <CountCard label="Needs verification" value={c.needs_verification || 0} testId="count-needs-verification" />
        <CountCard label="Suspended" value={c.suspended || 0} testId="count-suspended" />
        <CountCard label="Rejected" value={c.rejected || 0} testId="count-rejected" />
        <CountCard label="Open complaints" value={c.open_complaints || 0} testId="count-open-complaints" />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`oversight-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${filter === k ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700'}`}>{l}</button>
        ))}
      </div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm text-gray-700">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr><th className="p-3">Professional</th><th className="p-3">Status</th><th className="p-3">Credentials</th><th className="p-3">Verified on</th><th className="p-3">Standards</th><th className="p-3">Complaints</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-gray-500">No professionals here.</td></tr>}
            {rows.map(p => (
              <tr key={p.profile_id} className="border-t" data-testid={`oversight-row-${p.profile_id}`}>
                <td className="p-3"><p className="font-semibold text-gray-800">{p.name}</p><p className="text-xs text-gray-500">{p.company || p.professional_type?.replace('_', ' ')}{p.family_endorsed ? ' · Family-recommended' : ''}</p></td>
                <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[p.status] || 'bg-gray-100 text-gray-600'}`}>{STATUS_LABELS[p.status] || p.status}</span></td>
                <td className="p-3">{p.credentials_verified ? <span className="text-green-700">Verified</span> : <span className="text-amber-700">Not verified</span>}</td>
                <td className="p-3">{fmt(p.verified_at)}{p.verified_by_name ? ` · ${p.verified_by_name}` : ''}</td>
                <td className="p-3">{p.standards_accepted_at ? 'Accepted' : <span className="text-amber-700" data-testid="oversight-standards-pending">Not yet{p.status === 'approved' && ' — hidden from families'}</span>}</td>
                <td className="p-3">{p.open_complaints > 0 ? <span className="text-red-700 font-semibold">{p.open_complaints} open</span> : '—'}</td>
                <td className="p-3"><button onClick={() => setSelected(p.profile_id)} className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-xs" data-testid={`review-pro-${p.profile_id}`}>Review</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && <ProReviewDialog profileId={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}
